import { trigger, validateEmail } from "../js/utils"
import config from "../data/siteConfig"
import { appState, setAppState, removeAppState } from "../js/app"

let initialAuthCheck = false

const setLoginStatus = isUserSignedIn => {
  const authState = appState("auth-signed-in")

  if (isUserSignedIn) {
    setAppState("auth-signed-in")
  } else {
    removeAppState("auth-signed-in")
    localStorage.removeItem("auth")
  }

  // trigger a custom event:
  // - on the very first check regardless of the status
  // - or when the status has changed
  if (!initialAuthCheck || appState("auth-signed-in") !== authState) {
    trigger("auth:loginStatus")
    initialAuthCheck = true
  }
}

const signin = async body => {
  const url = `${config.BACKEND}/login?format=json`
  const formData = new FormData()
  formData.append("username", body.name)
  formData.append("password", body.pass)
  const resp = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      // "Content-Type": "application/json;charset=UTF-8",
      Accept: "application/vnd.api+json",
    },
    body: formData,
  })

  const respData = await resp.json()
  if (resp.status === 200) {
    localStorage.setItem("auth", JSON.stringify(respData))
    trigger("auth:signedIn")
  } else {
    throw Error("Wrong username / password")
  }
}

const signout = async () => {
  const authData = JSON.parse(localStorage.getItem("auth")) || {}
  const url = `${appState("is-dev") ? config.BACKEND_DEV : config.BACKEND}/users/logout`
  const resp = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      // "Content-Type": "application/json",
      // "X-CSRF-Token": authData.csrf_token,
    },
  })
  trigger("auth:signedOut")
  setLoginStatus(false)
}

const signup = async body => {
  const url = `${appState("is-dev") ? config.BACKEND_DEV : config.BACKEND}/users/register`
  // console.log(rdata)
  const resp = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  })

  const respData = await resp.json()
  if (resp.status === 200) {
    return respData
  }

  throw respData.message.replace(/(?:\r\n|\r|\n)/g, "<br />")
}

const forgot = async val => {
  const body = {
    data: {
      type: "user--password-reset",
      attributes: {},
    },
  }

  if (validateEmail(val)) body.data.attributes.mail = val
  else body.data.attributes.name = val

  const url = `${appState("is-dev") ? config.BACKEND_DEV : config.BACKEND}/jsonapi/user/password/reset`
  const resp = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/vnd.api+json",
      Accept: "application/vnd.api+json",
    },
    body: JSON.stringify(body),
  })

  const respData = resp.json()
  if (resp.status !== 202) {
    throw Error(respData.errors[0].detail)
  }

  return respData
}

const resetPassword = async pass => {
  if (window.location.pathname.indexOf("/user/reset/") === -1) throw Error()

  const credentialKeys = ["user", "timestamp", "hash"]
  const pathArray = window.location.pathname.split("/user/reset/")[1].split("/")
  const credentials = credentialKeys.reduce((acc, value, i) => {
    acc[value] = pathArray[i]
    return acc
  }, {})
  credentials.pass = pass
  const url = `${appState("is-dev") ? config.BACKEND_DEV : config.BACKEND}/jsonapi/user/${
    credentials.user
  }/password/update`
  delete credentials.user

  const body = {
    data: {
      type: "user--user",
      attributes: credentials,
    },
  }

  const resp = await fetch(url, {
    method: "PATCH",
    credentials: "include",
    headers: {
      "Content-Type": "application/vnd.api+json",
      Accept: "application/vnd.api+json",
    },
    body: JSON.stringify(body),
  })

  const respData = resp.json()
  if (resp.status === 200) {
    return respData
  }

  throw Error(respData.message)
}

const getUserStatus = async () => {
  const url = `${appState("is-dev") ? config.BACKEND_DEV : config.BACKEND}/api/users/login_status`
  const resp = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json;charset=UTF-8",
    },
  })

  const respBody = await resp.text()
  const signedIn = resp.status === 200 && respBody === "1"
  setLoginStatus(signedIn)
  return signedIn
}

const requestUserData = async id => {
  // check localstorage auth data
  const url = `${appState("is-dev") ? config.BACKEND_DEV : config.BACKEND}/jsonapi/user/user/${id}`
  const resp = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/vnd.api+json",
      Accept: "application/vnd.api+json",
    },
  })

  return resp
}

const getUserId = async () => {
  const url = `${appState("is-dev") ? config.BACKEND_DEV : config.BACKEND}/jsonapi`
  const resp = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/vnd.api+json",
      Accept: "application/vnd.api+json",
    },
  })

  const respData = await resp.json()
  if (resp.status === 200) {
    return respData.meta.links.me.meta.id
  }

  throw respData.message
}

const querySignedInUser = async () => {
  const authData = JSON.parse(localStorage.getItem("auth")) || {}

  const userIsAlreadySignedIn = await getUserStatus()

  if (userIsAlreadySignedIn) {
    setLoginStatus(true)

    // TODO
    // currently the server not returns user related date due to CORS error
    /* const id = await getUserId()
    const resp = await requestUserData(id)
    const respData = await resp.json()

    if (resp.status === 200) {
      if (!authData.current_user) authData.current_user = {}

      authData.current_user.id = respData.data.id
      authData.current_user.name = respData.data.attributes.name

      // store the user details
      localStorage.setItem("auth", JSON.stringify(authData))
      setLoginStatus(true)
      return respData
    }

    setLoginStatus(false)
    throw respData */
  } else {
    // user is not signed in
    setLoginStatus(false)
  }
}

export default {
  signin,
  signout,
  signup,
  getUserStatus,
  forgot,
  resetPassword,
  querySignedInUser,
}
